import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import ZoneChecker from '../components/common/ZoneChecker';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Check, ArrowRight, ArrowLeft, Calendar, MapPin,
  Users, Package, Plus, Minus, Sparkles, HelpCircle,
  Clock, CheckSquare, Star, Tag, Wallet, FileText,
  Download, Phone, Mail, AlertCircle, Shield, User,
  ChevronDown, ChevronUp, RefreshCw, TrendingUp, Edit3,
  Search, Grid, Filter, ShoppingCart, X, Layers, Truck
} from 'lucide-react';
import { formatDate } from '../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;


// Constantes pour l'étape 6 — boutique intelligente
const PACK_CONFIGS = [
  { id: 'pack-anniversaire-exterieur', label: '🎂 Anniversaire Extérieur', description: 'Jeux + Gourmandises — succès garanti en plein air', event_types: ['anniversaire_enfant','anniversaire_adulte','fete_privee'], produit_noms: ['Jeu gonflable Pirate', 'Machine à barbe à papa', 'Machine à popcorn', 'Cornhole', 'Tables pliantes'], color: '#F4A7B2', badge: 'Recommandé' },
  { id: 'pack-mariage-ambiance', label: '💍 Mariage Ambiance', description: 'Photo + animation + convivialité pour un mariage mémorable', event_types: ['mariage'], produit_noms: ['Photobooth', 'Cornhole', 'Mini golf', 'Brasero à bois', 'Enceinte JBL'], color: '#5DBCBF', badge: 'Premium' },
  { id: 'pack-entreprise-team', label: '💼 Team Building', description: 'Challenge + photo pour vos événements professionnels', event_types: ['entreprise','cocktail_entreprise'], produit_noms: ['Photobooth 360', 'Mini golf', 'Puissance 4', 'Enceinte JBL'], color: '#7DB1FF', badge: 'Business' },
  { id: 'pack-gourmand', label: '🍬 Animations Gourmandes', description: 'Barbe à papa, popcorn et surprises sucrées', event_types: ['anniversaire_enfant','bapteme','fete_privee','garden_party'], produit_noms: ['Machine à barbe à papa', 'Machine à popcorn', 'Gaufrier bubble', 'Mini pancake'], color: '#D4AF37', badge: 'Populaire' },
];

// Normalise un string (enlève les accents) pour comparaison robuste
function norm(s) {
  return (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

// Catégorisation des produits par nom (côté frontend) — étape 6
function getCatProduit(p) {
  const n = norm(p.nom);
  if (/photobooth|borne.*photo|cabine.*photo|backdrop|360/.test(n)) return '📸 Photobooth';
  if (/gonflable/.test(n)) return '🎠 Jeux gonflables';
  if (/mini golf|puissance 4|cornhole|billard|chamboule|peche.*canard|fleche|palet petanque|gabaky|schmolle|shuffle|passe trappe|palets|air hockey/.test(n)) return '🎯 Jeux en bois & animations';
  if (/barbe.*papa|popcorn|gaufrier|pancake|friteuse|blender|smoothie/.test(n)) return '🍬 Gourmandises & Food';
  if (/tireuse|biere|brasero.*bois|glacons|percolateur|cafetiere/.test(n)) return '🍺 Boissons & Bar';
  if (/enceinte|jbl|videoprojecteur|video.*projecteur|livre.*or|projection|micro/.test(n)) return '🎵 Son & Animation';
  if (/barnum|table.*pliante|chaises? pliante|table haute|remorque frig/.test(n)) return '🪑 Mobilier';
  if (/arche|decoration/.test(n)) return '🌸 Décoration';
  return null;
}

const CAT_ORDER_6 = ['📸 Photobooth','🎠 Jeux gonflables','🎯 Jeux en bois & animations','🍬 Gourmandises & Food','🍺 Boissons & Bar','🎵 Son & Animation','🪑 Mobilier','🌸 Décoration'];

// Catégories pour étape 7 (produits complémentaires)
function getCatStep7(p) {
  const n = norm(p.nom);
  if (/barbe.*papa|popcorn|gaufrier|pancake|friteuse|blender/.test(n)) return '🍬 Gourmandises';
  if (/tireuse|biere|brasero.*bois|glacons|percolateur|cafetiere/.test(n)) return '🍺 Boissons & Bar';
  if (/enceinte|jbl|videoprojecteur|video.*projecteur|livre.*or|micro/.test(n)) return '🎵 Son & Animation';
  if (/barnum|table.*pliante|chaises?|table haute/.test(n)) return '🪑 Mobilier';
  if (/arche|decoration/.test(n)) return '🌸 Décoration';
  if (/photobooth|borne|cabine|backdrop/.test(n)) return '📸 Photobooth';
  if (/livraison|installation|demontage|surveillance|animation|food.truck|bar.*theme|grazing|brasero.*event/.test(n)) return '🎭 Prestations';
  return null;
}

const CAT_ORDER_7 = ['🍬 Gourmandises','🍺 Boissons & Bar','🎵 Son & Animation','🪑 Mobilier','🌸 Décoration','📸 Photobooth','🎭 Prestations'];

const FILTERS_6 = [
  { id: 'tous', label: 'Tout' },
  { id: 'bestseller', label: '⭐ Best sellers' },
  { id: 'exterieur', label: '🌿 Extérieur' },
  { id: 'enfants', label: '🎂 Enfants' },
];

const CAT_ORDER = ['Jeux gonflables','Jeux en bois','Animations gourmandes','Photobooth','Mobilier','Son & lumière','Ambiance','Décoration','Gourmandises'];

// ── Composant ProductCard ── (défini HORS du composant parent)
function ProductCard6({ p, selected, onToggle, unavailable = false }) {
  const isLocation = p.type === 'location' || p.type === 'fabrication' || p.type === 'prestation';
  return (
    <button onClick={() => onToggle(p)} data-testid={`p6-${p.id}`}
      className="flex gap-3 p-3 rounded-2xl text-left w-full transition-all relative"
      style={{
        background: selected ? 'var(--teal-mist)' : '#FFFFFF',
        border: `${selected ? '2px' : '1.5px'} solid ${unavailable ? 'rgba(217,75,93,0.35)' : (selected ? 'var(--teal)' : 'var(--border)')}`,
        boxShadow: selected ? '0 4px 16px rgba(93,188,191,0.15)' : '0 1px 4px rgba(26,26,46,0.04)',
        cursor: 'pointer',
        opacity: unavailable && !selected ? 0.72 : 1,
      }}>
      <div className="relative flex-shrink-0">
        <img src={p.images?.[0] || 'https://images.unsplash.com/photo-1531058020387-3be344556be6?w=150&q=70'}
          alt={p.nom} className="w-14 h-14 rounded-xl object-cover" loading="lazy"
          style={{ filter: unavailable ? 'grayscale(0.6)' : 'none' }} />
        {p.featured && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
            style={{ background: '#D4AF37', fontSize: 9, color: '#0B1E20', fontWeight: 700 }}>★</span>
        )}
        {selected && (
          <div className="absolute inset-0 rounded-xl flex items-center justify-center"
            style={{ background: 'rgba(93,188,191,0.35)' }}>
            <Check size={18} style={{ color: '#FFFFFF' }} />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-xs leading-snug mb-0.5"
          style={{ color: selected ? '#0B4B4E' : 'var(--ink)' }}>{p.nom}</p>
        {unavailable && (
          <p className="text-xs font-semibold flex items-center gap-1 mb-0.5" data-testid={`p6-unavailable-${p.id}`}
            style={{ color: '#D94B5D', fontSize: 10 }}>
            ⚠️ Pris à votre date
          </p>
        )}
        {!unavailable && p.ia_reason && (
          <p className="text-xs italic mb-1" style={{ color: '#5DBCBF', fontSize: 10, lineHeight: 1.3 }}>
            <Sparkles size={8} className="inline mr-0.5" />{p.ia_reason.slice(0, 55)}…
          </p>
        )}
        <div className="flex items-center justify-between mt-1">
          {isLocation
            ? <span className="text-xs font-semibold" style={{ color: '#0B4B4E' }}>Sur devis</span>
            : <span className="price-gold text-sm font-bold">{p.prix_profil || p.tarifs?.particulier || 0}€</span>}
          <span className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0"
            style={{
              background: unavailable && !selected ? 'rgba(217,75,93,0.1)' : (selected ? 'var(--teal)' : 'var(--bg)'),
              color: unavailable && !selected ? '#D94B5D' : (selected ? '#0B1E20' : 'var(--text-muted)'),
              fontSize: 9,
            }}>
            {selected ? 'Sélectionné ✓' : unavailable ? 'Indisponible' : '+ Ajouter'}
          </span>
        </div>
      </div>
    </button>
  );
}

// ── BOUTIQUE ÉVÉNEMENTIELLE — Composant principal étape 6 ──
// Affichage par catégories de TOUS les produits + packs suggérés
function BoutiqueEvenementielle({
  session, suggestions, autoPacks, allProducts, budgetRanges,
  loadingSuggestions, isSelected, toggleProduct, selectedEvent,
  filterActive, setFilterActive, searchQuery, setSearchQuery,
  expandedCat, setExpandedCat, unavailableMap = {},
}) {
  const nbSelected = session.produits.length;
  const eventType = session.type_evenement;

  const relevantPacks = PACK_CONFIGS.filter(pack =>
    pack.event_types.includes(eventType) || pack.event_types.includes('tous')
  );

  function addPack(pack, allProds) {
    const packProducts = allProds.filter(p => pack.produit_noms.some(n => p.nom.toLowerCase().includes(n.toLowerCase())));
    packProducts.forEach(p => { if (!isSelected(p.id)) toggleProduct(p); });
    toast.success(`Pack "${pack.label}" ajouté !`, { description: `${packProducts.length} animation(s) sélectionnée(s)` });
  }

  // Regrouper les produits par catégorie (avec getCatProduit)
  const byCategory6 = useMemo(() => {
    const cats = {};
    allProducts.forEach(p => {
      const cat = getCatProduit(p);
      if (!cat) return; // ignorer les produits non catégorisés (boutique, etc.)
      if (!cats[cat]) cats[cat] = [];
      cats[cat].push(p);
    });
    return cats;
  }, [allProducts]);

  const orderedCats6 = useMemo(() => {
    const available = Object.keys(byCategory6);
    const ordered = CAT_ORDER_6.filter(c => available.includes(c));
    const rest = available.filter(c => !CAT_ORDER_6.includes(c));
    return [...ordered, ...rest];
  }, [byCategory6]);

  // Filtrage recherche
  const filteredProducts = useMemo(() => {
    if (!searchQuery && filterActive === 'tous') return null;
    return allProducts.filter(p => {
      if (!getCatProduit(p)) return false;
      if (searchQuery && !p.nom.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (filterActive === 'tous') return true;
      if (filterActive === 'bestseller') return p.featured;
      if (filterActive === 'exterieur') return (p.tags || []).some(t => ['extérieur','outdoor','exterieur'].includes(t));
      if (filterActive === 'enfants') return (p.tags || []).some(t => ['enfant','enfants','anniversaire'].includes(t));
      return true;
    });
  }, [allProducts, filterActive, searchQuery]);

  return (
    <div className="animate-fade-in-up" style={{ paddingBottom: 80 }}>
      {/* Header */}
      <div className="text-center mb-4">
        <h2 className="font-display text-lg font-bold mb-0.5" style={{ color: 'var(--ink)' }}>
          Choisissez vos animations
        </h2>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {selectedEvent?.label} — {Object.values(byCategory6).reduce((s,a) => s+a.length, 0)} animations disponibles
        </p>
      </div>

      {/* Recherche */}
      <div className="relative mb-3">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
        <input type="text" placeholder="Rechercher une animation…" value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm"
          style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
            <X size={14} style={{ color: 'var(--text-muted)' }} />
          </button>
        )}
      </div>

      {/* Filtres rapides */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 no-scrollbar">
        {FILTERS_6.map(f => (
          <button key={f.id} onClick={() => setFilterActive(f.id)}
            className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
            style={{
              background: filterActive === f.id ? 'var(--teal)' : '#FFFFFF',
              color: filterActive === f.id ? '#0B1E20' : 'var(--text-muted)',
              border: `1px solid ${filterActive === f.id ? 'var(--teal)' : 'var(--border)'}`,
            }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Vue filtrée */}
      {filteredProducts ? (
        <div className="mb-4">
          <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
            {filteredProducts.length} résultat{filteredProducts.length > 1 ? 's' : ''}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {filteredProducts.map(p => (
              <ProductCard6 key={p.id} p={p} selected={isSelected(p.id)} onToggle={toggleProduct} unavailable={!!unavailableMap[p.id]} />
            ))}
            {filteredProducts.length === 0 && (
              <p className="col-span-2 text-center text-sm py-8" style={{ color: 'var(--text-muted)' }}>Aucun produit trouvé</p>
            )}
          </div>
        </div>
      ) : allProducts.length === 0 ? (
        <div className="flex items-center justify-center py-10">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 rounded-full mx-auto mb-3"
              style={{ border: '2px solid var(--border)', borderTopColor: 'var(--teal)' }} />
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Chargement du catalogue…</p>
          </div>
        </div>
      ) : (
        /* ═══ VUE PAR CATÉGORIES (mode par défaut) ═══ */
        <div className="space-y-3 mb-6">
          {orderedCats6.map(cat => {
            const prods = byCategory6[cat] || [];
            const isOpen = expandedCat[cat] !== false;
            const nbSel = prods.filter(p => isSelected(p.id)).length;
            return (
              <div key={cat} className="rounded-2xl overflow-hidden"
                style={{ border: `1.5px solid ${nbSel > 0 ? 'rgba(93,188,191,0.4)' : 'var(--border)'}` }}>
                <button onClick={() => setExpandedCat(p => ({ ...p, [cat]: !isOpen }))}
                  className="w-full flex items-center justify-between px-4 py-3"
                  style={{ background: nbSel > 0 ? 'rgba(93,188,191,0.04)' : '#FFFFFF' }}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{cat}</p>
                    <span className="text-xs px-2 py-0.5 rounded-full"
                      style={{ background: 'var(--teal-mist)', color: '#0B4B4E' }}>
                      {prods.length}
                    </span>
                    {nbSel > 0 && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                        style={{ background: 'rgba(31,157,122,0.1)', color: '#1F9D7A' }}>
                        ✓ {nbSel} ajouté{nbSel > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  {isOpen ? <ChevronUp size={15} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={15} style={{ color: 'var(--text-muted)' }} />}
                </button>
                {isOpen && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3"
                    style={{ background: 'var(--bg)', borderTop: '1px solid var(--border)' }}>
                    {prods.map(p => (
                      <ProductCard6 key={p.id} p={p} selected={isSelected(p.id)} onToggle={toggleProduct} unavailable={!!unavailableMap[p.id]} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Packs suggérés (secondaire, collapsible) ─── */}
      {relevantPacks.length > 0 && (
        <div className="mb-6">
          <button
            onClick={() => setExpandedCat(p => ({ ...p, '__packs__': p['__packs__'] !== false ? false : true }))}
            className="flex items-center gap-2 mb-3 w-full text-left"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <Layers size={13} style={{ color: '#D4AF37' }} />
            <p className="text-xs font-bold" style={{ color: '#D4AF37' }}>Packs suggérés pour votre événement</p>
            {expandedCat['__packs__'] === false
              ? <ChevronDown size={13} style={{ color: '#D4AF37' }} />
              : <ChevronUp size={13} style={{ color: '#D4AF37' }} />}
          </button>
          {expandedCat['__packs__'] !== false && (
            <div className="flex flex-col gap-3">
              {relevantPacks.map(pack => {
                const packProds = allProducts.filter(p => pack.produit_noms.some(n => p.nom.toLowerCase().includes(n.toLowerCase())));
                const allAdded = packProds.length > 0 && packProds.every(p => isSelected(p.id));
                return (
                  <div key={pack.id} className="rounded-2xl overflow-hidden"
                    style={{ border: `1.5px solid ${pack.color}35`, background: `${pack.color}06` }}>
                    <div className="flex items-center justify-between px-4 py-2.5"
                      style={{ background: `${pack.color}15`, borderBottom: `1px solid ${pack.color}20` }}>
                      <div>
                        <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>{pack.label}</p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{pack.description}</p>
                      </div>
                      <button
                        onClick={() => allAdded ? packProds.forEach(p => isSelected(p.id) && toggleProduct(p)) : addPack(pack, allProducts)}
                        className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold flex-shrink-0 ml-3"
                        style={{
                          background: allAdded ? 'rgba(217,75,93,0.1)' : pack.color,
                          color: allAdded ? '#D94B5D' : '#0B1E20',
                          border: allAdded ? '1px solid rgba(217,75,93,0.2)' : 'none',
                        }}>
                        {allAdded ? <><X size={12} /> Retirer</> : <><Plus size={12} /> Ajouter</>}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5 px-4 py-2">
                      {pack.produit_noms.map((n, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded-full"
                          style={{ background: `${pack.color}15`, color: 'var(--ink)' }}>
                          {n.split(' ').slice(0, 3).join(' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Panier flottant */}
      {nbSelected > 0 && (
        <div style={{
          position: 'fixed', bottom: 76, left: 16, right: 16, zIndex: 8980,
          background: 'linear-gradient(135deg, #0B4B4E, #5DBCBF)',
          borderRadius: 18, padding: '12px 18px',
          boxShadow: '0 12px 40px rgba(11,75,78,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          maxWidth: 560, margin: '0 auto',
        }}>
          <div className="flex items-center gap-3">
            <div className="relative">
              <ShoppingCart size={20} style={{ color: '#FFFFFF' }} />
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ background: '#D4AF37', color: '#0B1E20' }}>{nbSelected}</span>
            </div>
            <p className="text-xs font-bold" style={{ color: '#FFFFFF' }}>
              {nbSelected} animation{nbSelected > 1 ? 's' : ''} sélectionnée{nbSelected > 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {session.produits.slice(-2).map(p => (
              <button key={p.product_id}
                onClick={() => toggleProduct({ id: p.product_id, nom: p.product_nom })}
                className="flex items-center gap-1 px-2 py-1 rounded-full text-xs"
                style={{ background: 'rgba(255,255,255,0.2)', color: '#FFFFFF', fontSize: 10 }}>
                {p.product_nom.split(' ').slice(0, 2).join(' ')} ✕
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



/* ───────────────────────────────────────────────
   TYPES D'ÉVÉNEMENTS
─────────────────────────────────────────────── */
// Événements pour particuliers
const EVENT_TYPES_PARTICULIER = [
  { id: 'anniversaire_enfant', label: 'Anniversaire enfant', emoji: '🎂', desc: 'Pour les 3-17 ans' },
  { id: 'anniversaire_adulte', label: 'Anniversaire adulte', emoji: '🎉', desc: 'À partir de 18 ans' },
  { id: 'mariage', label: 'Mariage', emoji: '💍', desc: 'Votre Jour J' },
  { id: 'bapteme', label: 'Baptême / Communion', emoji: '🕊', desc: 'Moment de grâce' },
  { id: 'fete_privee', label: 'Fête privée', emoji: '🎊', desc: 'Entre amis' },
  { id: 'communion', label: 'Communion', emoji: '✨', desc: 'Moment solennel' },
];

// Événements pro / commerciaux (entreprise ou commerce identifié)
const EVENT_TYPES_PRO = [
  { id: 'cocktail_entreprise', label: 'Cocktail entreprise', emoji: '🥂', desc: 'Networking, inauguration' },
  { id: 'team_building', label: 'Team building', emoji: '🤝', desc: "Cohésion d'équipe" },
  { id: 'lancement_produit', label: 'Lancement produit', emoji: '🚀', desc: 'Mise en avant produit' },
  { id: 'soiree_vip', label: 'Soirée VIP', emoji: '⭐', desc: 'Clients premium' },
  { id: 'animation_commerciale', label: 'Animation commerciale', emoji: '📣', desc: 'Attirer la clientèle' },
  { id: 'inauguration', label: 'Inauguration', emoji: '🎗', desc: 'Ouverture / Portes ouvertes' },
  { id: 'vente_privee', label: 'Vente privée', emoji: '🛍', desc: 'Événement exclusif' },
  { id: 'evenement_magasin', label: 'Événement magasin', emoji: '🏪', desc: 'Animation point de vente' },
  { id: 'soldes', label: 'Soldes / Promo', emoji: '🏷', desc: 'Animation commerciale' },
  { id: 'portes_ouvertes', label: 'Portes ouvertes', emoji: '🚪', desc: 'Visite & découverte' },
];

// Fusion pour usage interne
const EVENT_TYPES = [...EVENT_TYPES_PARTICULIER, ...EVENT_TYPES_PRO];

const STEPS = [
  { id: 1,  label: 'Événement',   icon: <Star size={14} /> },
  { id: 2,  label: 'Date',        icon: <Calendar size={14} /> },
  { id: 3,  label: 'Lieu',        icon: <MapPin size={14} /> },
  { id: 4,  label: 'Invités',     icon: <Users size={14} /> },
  { id: 5,  label: 'Audit',       icon: <HelpCircle size={14} /> },
  { id: 6,  label: 'Animations',  icon: <Package size={14} /> },
  { id: 7,  label: 'Compléments', icon: <ShoppingCart size={14} /> },
  { id: 8,  label: 'Récap',       icon: <CheckSquare size={14} /> },
  { id: 9,  label: 'Devis',       icon: <FileText size={14} /> },
  { id: 10, label: 'Envoi',       icon: <Check size={14} /> },
];

/* ── Stepper 10 étapes — version compacte responsive ── */
function StepIndicator({ current }) {
  // Sur mobile : afficher numéro actuel + barre de progression
  // Sur desktop : afficher tous les steps condensés
  const pct = Math.round(((current - 1) / (STEPS.length - 1)) * 100);

  return (
    <div className="mb-6 px-2">
      {/* Barre de progression (toujours visible) */}
      <div className="relative w-full h-1.5 rounded-full mb-3" style={{ background: 'var(--border)' }}>
        <div className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, background: 'linear-gradient(90deg, var(--teal), #1F9D7A)' }} />
      </div>

      {/* Étapes — compact sur mobile, complet sur desktop */}
      <div className="flex items-center justify-between overflow-x-auto no-scrollbar" style={{ gap: 2 }}>
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <div className="flex flex-col items-center flex-shrink-0" style={{ minWidth: 0 }}>
              <div
                className="flex items-center justify-center font-bold transition-all"
                style={{
                  width: 28, height: 28, borderRadius: '50%', fontSize: 11,
                  background: s.id === current ? 'var(--teal)' :
                    s.id < current ? 'rgba(31,157,122,0.15)' : '#FFFFFF',
                  color: s.id === current ? '#0B1E20' :
                    s.id < current ? '#1F9D7A' : 'rgba(26,26,46,0.35)',
                  border: s.id < current ? '1.5px solid rgba(31,157,122,0.4)' :
                    s.id === current ? 'none' : '1.5px solid var(--border)',
                  boxShadow: s.id === current ? '0 0 0 3px rgba(93,188,191,0.2)' : 'none',
                }}>
                {s.id < current ? <Check size={12} /> : s.id}
              </div>
              {/* Label — uniquement pour étape active sur mobile, visible sur sm+ */}
              <span
                className="mt-1 text-center font-medium"
                style={{
                  fontSize: 9,
                  maxWidth: 44,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  // Sur mobile : visible uniquement pour actif et fait
                  opacity: s.id === current || s.id < current ? 1 : 0,
                  color: s.id === current ? 'var(--teal)' :
                    s.id < current ? '#1F9D7A' : 'rgba(26,26,46,0.3)',
                  fontWeight: s.id === current ? 700 : 400,
                  // Cacher les labels des étapes futures
                  visibility: s.id > current + 1 ? 'hidden' : 'visible',
                  height: 12,
                }}>
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className="flex-1 h-px" style={{
                minWidth: 4, maxWidth: 16,
                background: s.id < current ? '#1F9D7A' : 'var(--border)',
                marginBottom: 14,
              }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Indicateur textuel étape courante */}
      <p className="text-center mt-1" style={{ fontSize: 11, color: 'rgba(26,26,46,0.4)' }}>
        Étape {current} sur {STEPS.length} — {STEPS.find(s => s.id === current)?.label}
      </p>
    </div>
  );
}

/* ── Address Autocomplete (api-adresse.data.gouv.fr - gratuit, sans clé) ── */
function AddressAutocomplete({ value, onChange, onSelect }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const timer = useRef(null);

  async function search(q) {
    if (q.length < 3) { setSuggestions([]); return; }
    try {
      const res = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}&limit=5`);
      const data = await res.json();
      setSuggestions(data.features || []);
      setShowSug(true);
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
  }

  function handleChange(e) {
    const v = e.target.value;
    onChange(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(v), 350);
  }

  function handleSelect(feat) {
    const addr = feat.properties.label;
    onChange(addr);
    setSuggestions([]);
    setShowSug(false);
    if (onSelect) onSelect({
      label: addr,
      city: feat.properties.city,
      postcode: feat.properties.postcode,
      lat: feat.geometry.coordinates[1],
      lon: feat.geometry.coordinates[0],
    });
  }

  return (
    <div className="relative">
      <div className="relative">
        <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2"
          style={{ color: 'var(--text-muted)' }} />
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={() => suggestions.length && setShowSug(true)}
          onBlur={() => setTimeout(() => setShowSug(false), 200)}
          placeholder="Adresse, ville ou code postal..."
          className="w-full pl-9 pr-4 py-3 rounded-xl text-sm"
          style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }}
        />
      </div>
      {showSug && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-xl overflow-hidden"
          style={{ background: '#FFFFFF', border: '1px solid var(--border)', boxShadow: '0 12px 32px rgba(26,26,46,0.12)' }}>
          {suggestions.map((f, i) => (
            <button key={`list-${i}`} onMouseDown={() => handleSelect(f)}
              className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-[var(--bg)]"
              style={{ color: 'var(--ink)', borderBottom: i < suggestions.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <MapPin size={12} style={{ color: 'var(--teal)', flexShrink: 0 }} />
              <div>
                <span className="font-medium">{f.properties.label}</span>
                {f.properties.postcode && (
                  <span className="ml-1.5 text-xs" style={{ color: 'var(--text-muted)' }}>{f.properties.postcode}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   CONFIGURATEUR PRINCIPAL
───────────────────────────────────────────── */
export default function ConfigurateurPage() {
  const [step, setStep] = useState(1);
  const [audience, setAudience] = useState('all'); // 'all' | 'particulier' | 'pro'
  const [session, setSession] = useState({
    type_evenement: '',
    date_evenement: '', noDate: false,
    heure_debut: '14:00', heure_fin: '18:00',
    date_livraison: '', heure_livraison: '12:00',
    date_reprise: '', heure_reprise: '20:00',
    adresse: '', noAddress: false, acces: '', etage: '', lieu_type: 'interieur',
    adresse_coords: null, zone_info: null,
    nb_adultes: 20, nb_enfants: 0,
    produits: [], client_profile: 'particulier', notes: '',
    option_livraison: '',
    smart_answers: {}
  });

  const [smartQuestions, setSmartQuestions] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [autoPacks, setAutoPacks] = useState([]);
  const [budgetRanges, setBudgetRanges] = useState(null);
  // Étape 6 — boutique intelligente
  const [allProducts, setAllProducts] = useState([]);
  const [filterActive, setFilterActive] = useState('tous');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCat, setExpandedCat] = useState({});
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [creatingQuote, setCreatingQuote] = useState(false);
  const [activePackId, setActivePackId] = useState(null);
  const [showPackDetail, setShowPackDetail] = useState(null);
  // Produits à vendre associés aux animations sélectionnées (étape 7)
  const [associesConfig, setAssociesConfig] = useState([]);
  // Étape 7 — récapitulatif
  const [showBudgetRange, setShowBudgetRange] = useState(false);
  // Étape 8 — devis
  const [quoteMode, setQuoteMode] = useState(null); // 'contact' | 'acompte'
  const [clientForm, setClientForm] = useState({
    nom: '', prenom: '', email: '', telephone: '', notes: ''
  });
  // Étape 9 — sauvegarde
  const [createdQuote, setCreatedQuote] = useState(null);
  const [showRegister, setShowRegister] = useState(false);
  const [registerForm, setRegisterForm] = useState({ email: '', password: '', nom: '', prenom: '' });
  const [registering, setRegistering] = useState(false);
  const { setConfiguratorSession, addItem, cartItems } = useCart();
  const { user, login } = useAuth();
  const navigate = useNavigate();

  // Profil client effectif
  const clientProfile = user?.profile || 'particulier';
  const hasSpecialProfile = user && ['ce', 'entreprise', 'association', 'groupe'].includes(user.profile);

  // ── Transport auto-calculé selon la distance GPS depuis Figeac ──
  const [transport, setTransport] = useState(null);
  const [transportLoading, setTransportLoading] = useState(false);

  useEffect(() => {
    const coords = session.adresse_coords;
    if (!coords || coords.lat == null || coords.lon == null) { setTransport(null); return; }
    let cancelled = false;
    setTransportLoading(true);
    fetch(`${BACKEND}/api/livraison/calculate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lat: coords.lat, lon: coords.lon,
        ville: coords.city, code_postal: coords.postcode,
      }),
    })
      .then(r => r.json())
      .then(d => { if (!cancelled) setTransport(d && !d.error ? d : null); })
      .catch(() => { if (!cancelled) setTransport(null); })
      .finally(() => { if (!cancelled) setTransportLoading(false); });
    return () => { cancelled = true; };
  }, [session.adresse_coords]);

  // ── Vérification proactive de disponibilité (récap étape 8) ──
  const [availability, setAvailability] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  useEffect(() => {
    if (step !== 8) return;
    const dateStart = session.date_livraison || session.date_evenement;
    const pids = session.produits.map(p => p.product_id).filter(Boolean);
    if (!dateStart || pids.length === 0) { setAvailability(null); return; }
    let cancelled = false;
    setAvailabilityLoading(true);
    fetch(`${BACKEND}/api/booking/check-availability`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        product_ids: pids,
        date_start: dateStart,
        date_end: session.date_reprise || dateStart,
        heure_livraison: session.heure_livraison || '09:00',
        heure_retour: session.heure_reprise || '10:00',
      }),
    })
      .then(r => r.json())
      .then(d => { if (!cancelled) setAvailability(d); })
      .catch(() => { if (!cancelled) setAvailability(null); })
      .finally(() => { if (!cancelled) setAvailabilityLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, session.date_livraison, session.date_evenement, session.date_reprise, session.produits]);

  // Map product_id → indisponible (pour badges récap)
  const unavailableMap = useMemo(() => {
    const m = {};
    (availability?.products || []).forEach(r => { if (!r.disponible) m[r.product_id] = r; });
    return m;
  }, [availability]);

  // ── Disponibilité des produits affichés à l'étape 6 (pastille "pris à votre date") ──
  const [step6Dispo, setStep6Dispo] = useState({});

  useEffect(() => {
    if (step !== 6) return;
    const dateStart = session.date_livraison || session.date_evenement;
    if (!dateStart) { setStep6Dispo({}); return; }
    // Vérifier uniquement les produits de type location réellement affichés
    const pids = (allProducts || [])
      .filter(p => p.type === 'location' || p.type === 'fabrication' || p.type === 'prestation')
      .map(p => p.id)
      .filter(Boolean);
    if (pids.length === 0) { setStep6Dispo({}); return; }
    let cancelled = false;
    fetch(`${BACKEND}/api/booking/check-availability`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        product_ids: pids,
        date_start: dateStart,
        date_end: session.date_reprise || dateStart,
        heure_livraison: session.heure_livraison || '09:00',
        heure_retour: session.heure_reprise || '10:00',
      }),
    })
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        const m = {};
        (d?.products || []).forEach(r => { if (!r.disponible) m[r.product_id] = true; });
        setStep6Dispo(m);
      })
      .catch(() => { if (!cancelled) setStep6Dispo({}); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, allProducts, session.date_livraison, session.date_evenement, session.date_reprise]);


  // ── Produits à vendre associés aux animations sélectionnées (étape 7) ──
  useEffect(() => {
    if (step !== 7) return;
    const pids = session.produits.map(p => p.product_id).filter(Boolean);
    if (pids.length === 0) { setAssociesConfig([]); return; }
    let cancelled = false;
    Promise.all(pids.map(pid =>
      fetch(`${BACKEND}/api/products/${pid}/associes`).then(r => r.json()).catch(() => ({ produits: [] }))
    )).then(results => {
      if (cancelled) return;
      const map = {};
      results.forEach(res => (res.produits || []).forEach(p => { map[p.id] = p; }));
      const selIds = new Set(session.produits.map(p => p.product_id));
      setAssociesConfig(Object.values(map).filter(p => !selIds.has(p.id)));
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, session.produits]);




  // Charger questions intelligentes dès que l'événement est choisi
  useEffect(() => {
    if (session.type_evenement) {
      fetch(`${BACKEND}/api/configurateur/smart-questions/${session.type_evenement}`)
        .then(r => r.json())
        .then(d => setSmartQuestions(d))
        .catch((err) => { /* eslint-disable-line no-empty */ });
    }
  }, [session.type_evenement]);

  // Charger suggestions à l'étape 6
  useEffect(() => {
    if (step === 6 && session.type_evenement) loadSuggestions();
  // eslint-disable-next-line
  }, [step]);

  async function loadSuggestions() {
    setLoadingSuggestions(true);
    try {
      // Charger suggestions IA + tous les produits en parallèle
      const [sugRes, allRes] = await Promise.all([
        fetch(`${BACKEND}/api/configurateur/smart-suggestions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event_type: session.type_evenement,
            nb_adultes: session.nb_adultes || 20,
            nb_enfants: session.nb_enfants || 0,
            answers: session.smart_answers || {},
            profile: clientProfile,
            lieu_type: session.lieu_type || 'interieur',
          })
        }),
        fetch(`${BACKEND}/api/products?limit=49&catalogue_officiel=true`),
      ]);
      const [sugData, allData] = await Promise.all([sugRes.json(), allRes.json()]);
      // Respecte l'interrupteur "en service" : on masque les produits hors service
      setSuggestions((sugData.products || []).filter(p => p.disponible !== false));
      setAutoPacks(sugData.auto_packs || []);
      setBudgetRanges(sugData.budget_ranges || null);
      setAllProducts((allData.products || []).filter(p => p.disponible !== false));
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
    setLoadingSuggestions(false);
  }

  // Add / remove produit
  function toggleProduct(product) {
    const exists = session.produits.find(p => p.product_id === product.id);
    if (exists) {
      setSession(prev => ({ ...prev, produits: prev.produits.filter(p => p.product_id !== product.id) }));
    } else {
      setSession(prev => ({
        ...prev,
        produits: [...prev.produits, { product_id: product.id, product_nom: product.nom, quantite: 1 }]
      }));
      addItem(product);
      toast.success(`${product.nom} ajouté`, { duration: 1500 });
    }
  }

  function isSelected(productId) {
    return session.produits.some(p => p.product_id === productId);
  }

  // Update smart answer
  function setAnswer(qId, value, isMulti = false) {
    setSession(prev => {
      const prev_answers = prev.smart_answers || {};
      if (isMulti) {
        const current = prev_answers[qId] || [];
        const updated = current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value];
        return { ...prev, smart_answers: { ...prev_answers, [qId]: updated } };
      }
      return { ...prev, smart_answers: { ...prev_answers, [qId]: value } };
    });
  }

  // Validation par étape
  function canGoNext() {
    switch (step) {
      case 1: return !!session.type_evenement;
      case 2: return session.noDate || !!session.date_evenement;
      case 3: return session.noAddress || !!session.adresse;
      case 4: return session.nb_adultes > 0 || session.nb_enfants > 0;
      case 5: return true;  // Audit = optionnel mais enrichissant
      case 6: return true;  // Animations optionnelles
      case 7: return true;  // Compléments optionnels
      case 8: return true;  // Récap toujours valide
      case 9: return !!(clientForm.prenom && clientForm.nom && clientForm.email && clientForm.telephone && quoteMode);
      default: return true;
    }
  }

  // Créer le devis (appelé depuis étape 8 → passage à étape 9)
  async function createQuote() {
    setCreatingQuote(true);
    try {
      const res = await fetch(`${BACKEND}/api/devis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session: {
            ...session,
            client_profile: clientProfile,
            client_nom: `${clientForm.prenom} ${clientForm.nom}`.trim(),
            client_email: clientForm.email,
            client_telephone: clientForm.telephone,
            client_notes: clientForm.notes,
          },
          client_profile: clientProfile,
          client_email: clientForm.email || user?.email || '',
          client_nom: `${clientForm.prenom || ''} ${clientForm.nom || ''}`.trim() || (user ? `${user.prenom || ''} ${user.nom || ''}`.trim() : ''),
          client_telephone: clientForm.telephone,
          mode: quoteMode, // 'contact' | 'acompte'
        })
      });
      const quote = await res.json();
      setCreatedQuote(quote);
      setConfiguratorSession(session);
      setStep(10);
      // Relance auto J+2 planifiée côté backend
      toast.success(`Devis créé ! Réf. ${quote.numero}`, { duration: 3000 });
    } catch (e) {
      toast.error('Erreur lors de la création du devis');
      if (process.env.NODE_ENV !== 'production') console.error(e);
    } finally {
      setCreatingQuote(false);
    }
  }

  const selectedEvent = EVENT_TYPES.find(e => e.id === session.type_evenement);

  /* ─── RENDU ─────────────────────────────── */
  return (
    <>
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />

      <div className="container-ose" style={{ paddingTop: '2rem', paddingBottom: '120px', maxWidth: '700px' }}>
        {/* Titre */}
        <div className="text-center mb-6">
          <h1 className="font-display text-2xl sm:text-3xl font-bold mb-1"
            style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
            Composez votre événement
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Quelques questions pour vous proposer les meilleures animations
          </p>
        </div>

        {/* Stepper */}
        <StepIndicator current={step} />

        {/* Résumé chips */}
        {(session.type_evenement || session.date_evenement || session.nb_adultes > 0) && (
          <div className="flex flex-wrap gap-1.5 mb-5 justify-center">
            {session.type_evenement && (
              <button onClick={() => setStep(1)} className="px-3 py-1 rounded-full text-xs font-medium"
                style={{ background: 'var(--teal-mist)', color: '#0B4B4E', border: '1px solid rgba(93,188,191,0.3)' }}>
                {selectedEvent?.emoji} {selectedEvent?.label}
              </button>
            )}
            {(session.date_evenement || session.noDate) && (
              <button onClick={() => setStep(2)} className="px-3 py-1 rounded-full text-xs font-medium"
                style={{ background: 'rgba(212,175,55,0.1)', color: '#7A5E00', border: '1px solid rgba(212,175,55,0.25)' }}>
                {session.noDate ? '📅 Date à définir' : `📅 ${formatDate(session.date_evenement)}`}
              </button>
            )}
            {(session.adresse || session.noAddress) && (
              <button onClick={() => setStep(3)} className="px-3 py-1 rounded-full text-xs font-medium"
                style={{ background: 'var(--rose-mist)', color: '#B94B5C', border: '1px solid rgba(244,167,178,0.25)' }}>
                {session.noAddress ? '📍 Lieu à définir' : `📍 ${session.adresse.split(',')[0]}`}
              </button>
            )}
            {step > 4 && (session.nb_adultes > 0 || session.nb_enfants > 0) && (
              <button onClick={() => setStep(4)} className="px-3 py-1 rounded-full text-xs font-medium"
                style={{ background: 'rgba(139,92,246,0.08)', color: '#8B5CF6', border: '1px solid rgba(139,92,246,0.2)' }}>
                👥 {session.nb_adultes + session.nb_enfants} pers.
              </button>
            )}
          </div>
        )}

        {/* ─────────────────── ÉTAPE 1 — Type d'événement ─────────────────── */}
        {step === 1 && (
          <div className="animate-fade-in-up">
            <h2 className="font-display text-xl font-bold mb-5 text-center"
              style={{ color: 'var(--ink)' }}>Quel est votre événement ?</h2>
            {/* ── Toggle Particulier / Pro ── */}
            <div className="flex gap-2 mb-5 p-1 rounded-xl" style={{ background: 'var(--bg)' }}>
              {[
                { id: 'all', label: 'Tous' },
                { id: 'particulier', label: '🎉 Particuliers' },
                { id: 'pro', label: '💼 Entreprises & Commerces' },
              ].map(a => (
                <button key={a.id} onClick={() => setAudience(a.id)}
                  className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
                  style={{ background: audience === a.id ? 'var(--teal)' : 'transparent', color: audience === a.id ? '#0B1E20' : 'var(--text-muted)' }}>
                  {a.label}
                </button>
              ))}
            </div>

            <>
            {/* ── Particuliers ── */}
            {(audience === 'all' || audience === 'particulier') && (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-bold px-2.5 py-1 rounded-full"
                  style={{ background: 'var(--rose-mist)', color: '#B94B5C' }}>🎉 Particuliers</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {EVENT_TYPES_PARTICULIER.map(e => (
                  <button key={e.id}
                    onClick={() => setSession(prev => ({ ...prev, type_evenement: e.id }))}
                    className="p-4 rounded-2xl text-center transition-all"
                    style={{
                      background: session.type_evenement === e.id ? 'var(--teal-mist)' : '#FFFFFF',
                      border: session.type_evenement === e.id ? '2px solid var(--teal)' : '1.5px solid var(--border)',
                      boxShadow: session.type_evenement === e.id ? '0 6px 20px rgba(93,188,191,0.15)' : '0 2px 8px rgba(26,26,46,0.05)',
                      cursor: 'pointer'
                    }}>
                    <div className="text-3xl mb-2">{e.emoji}</div>
                    <div className="font-semibold text-xs" style={{ color: session.type_evenement === e.id ? 'var(--teal)' : 'var(--ink)' }}>{e.label}</div>
                    <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{e.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            )}

            {/* ── Entreprises / Commerces ── */}
            {(audience === 'all' || audience === 'pro') && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-bold px-2.5 py-1 rounded-full"
                  style={{ background: 'var(--teal-mist)', color: '#0B4B4E' }}>💼 Entreprises & Commerces</span>
                {!user && (
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    <a href="/connexion" style={{ color: 'var(--teal)' }}>Connectez-vous</a> pour accéder aux tarifs pro
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {EVENT_TYPES_PRO.map(e => (
                  <button key={e.id}
                    onClick={() => setSession(prev => ({ ...prev, type_evenement: e.id }))}
                    className="p-4 rounded-2xl text-center transition-all"
                    style={{
                      background: session.type_evenement === e.id ? 'var(--teal-mist)' : '#FFFFFF',
                      border: session.type_evenement === e.id ? '2px solid var(--teal)' : '1.5px solid var(--border)',
                      boxShadow: session.type_evenement === e.id ? '0 6px 20px rgba(93,188,191,0.15)' : '0 2px 8px rgba(26,26,46,0.05)',
                      cursor: 'pointer'
                    }}>
                    <div className="text-3xl mb-2">{e.emoji}</div>
                    <div className="font-semibold text-xs" style={{ color: session.type_evenement === e.id ? 'var(--teal)' : 'var(--ink)' }}>{e.label}</div>
                    <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{e.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            )}
            </>
          </div>
        )}

        {/* ─────────────────── ÉTAPE 2 — Date ─────────────────── */}
        {step === 2 && (
          <div className="animate-fade-in-up">
            <h2 className="font-display text-xl font-bold mb-5 text-center"
              style={{ color: 'var(--ink)' }}>Quand est votre événement ?</h2>
            <div className="space-y-4">
              {/* Option "pas de date" */}
              <button
                onClick={() => setSession(prev => ({ ...prev, noDate: !prev.noDate, date_evenement: prev.noDate ? prev.date_evenement : '' }))}
                className="flex items-center gap-3 w-full p-4 rounded-2xl text-left transition-all"
                style={{
                  background: session.noDate ? 'rgba(212,175,55,0.06)' : '#FFFFFF',
                  border: session.noDate ? '1.5px solid rgba(212,175,55,0.4)' : '1.5px solid var(--border)',
                }}>
                <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0"
                  style={{ background: session.noDate ? 'var(--gold)' : 'transparent', border: session.noDate ? 'none' : '1.5px solid var(--border)' }}>
                  {session.noDate && <Check size={12} color="#1A1A2E" />}
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>
                    Je n'ai pas encore de date
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    Nous vous aiderons à définir la date plus tard
                  </p>
                </div>
              </button>

              {!session.noDate && (
                <>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ink)' }}>
                        Date de l'événement *
                      </label>
                      <input type="date" value={session.date_evenement}
                        onChange={e => setSession(prev => ({ ...prev, date_evenement: e.target.value }))}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2.5 rounded-xl text-sm"
                        style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ink)' }}>Début</label>
                        <input type="time" value={session.heure_debut}
                          onChange={e => setSession(prev => ({ ...prev, heure_debut: e.target.value }))}
                          className="w-full px-3 py-2.5 rounded-xl text-sm"
                          style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ink)' }}>Fin</label>
                        <input type="time" value={session.heure_fin}
                          onChange={e => setSession(prev => ({ ...prev, heure_fin: e.target.value }))}
                          className="w-full px-3 py-2.5 rounded-xl text-sm"
                          style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                      </div>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ink)' }}>Date livraison</label>
                      <input type="date" value={session.date_livraison}
                        onChange={e => setSession(prev => ({ ...prev, date_livraison: e.target.value }))}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2.5 rounded-xl text-sm"
                        style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ink)' }}>Heure livraison</label>
                      <input type="time" value={session.heure_livraison}
                        onChange={e => setSession(prev => ({ ...prev, heure_livraison: e.target.value }))}
                        className="w-full px-3 py-2.5 rounded-xl text-sm"
                        style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ─────────────────── ÉTAPE 3 — Lieu ─────────────────── */}
        {step === 3 && (
          <div className="animate-fade-in-up">
            <h2 className="font-display text-xl font-bold mb-5 text-center"
              style={{ color: 'var(--ink)' }}>Où se déroule l'événement ?</h2>
            <div className="space-y-4">
              {/* Option "pas de lieu" */}
              <button
                onClick={() => setSession(prev => ({ ...prev, noAddress: !prev.noAddress }))}
                className="flex items-center gap-3 w-full p-4 rounded-2xl text-left transition-all"
                style={{
                  background: session.noAddress ? 'rgba(212,175,55,0.06)' : '#FFFFFF',
                  border: session.noAddress ? '1.5px solid rgba(212,175,55,0.4)' : '1.5px solid var(--border)',
                }}>
                <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0"
                  style={{ background: session.noAddress ? 'var(--gold)' : 'transparent', border: session.noAddress ? 'none' : '1.5px solid var(--border)' }}>
                  {session.noAddress && <Check size={12} color="#1A1A2E" />}
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>Je n'ai pas encore de lieu</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>On continue sans — pas de blocage</p>
                </div>
              </button>

              {!session.noAddress && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ink)' }}>
                      Adresse de l'événement *
                    </label>
                    <AddressAutocomplete
                      value={session.adresse}
                      onChange={v => setSession(prev => ({ ...prev, adresse: v }))}
                      onSelect={info => setSession(prev => ({
                        ...prev,
                        adresse: info.label,
                        adresse_coords: { lat: info.lat, lon: info.lon, city: info.city, postcode: info.postcode }
                      }))}
                    />
                  </div>

                  {/* Transport auto-calculé dès qu'une adresse est sélectionnée */}
                  {session.adresse_coords ? (
                    <div className="p-3.5 rounded-xl" data-testid="transport-auto-card"
                      style={{
                        background: transport?.livraison_offerte ? 'rgba(31,157,122,0.08)' : 'var(--teal-mist)',
                        border: `1.5px solid ${transport?.livraison_offerte ? 'rgba(31,157,122,0.4)' : 'rgba(93,188,191,0.3)'}`,
                      }}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <Truck size={15} style={{ color: 'var(--teal)' }} />
                        <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>
                          Transport (Livraison + Reprise)
                        </p>
                      </div>
                      {transportLoading ? (
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Calcul de la distance…</p>
                      ) : transport?.devis_requis ? (
                        <p className="text-xs" style={{ color: '#C58B19' }} data-testid="transport-devis">
                          {transport.message || 'Hors zone standard — tarif sur devis.'}
                        </p>
                      ) : transport ? (
                        <div data-testid="transport-result">
                          <div className="flex items-center justify-between">
                            <span className="text-xs" style={{ color: 'rgba(11,75,78,0.8)' }}>
                              📍 {transport.distance_km != null ? `${transport.distance_km} km de Figeac` : transport.zone_label}
                            </span>
                            <span className="text-base font-bold"
                              style={{ color: transport.livraison_offerte ? '#1F9D7A' : 'var(--gold, #C58B19)' }}>
                              {transport.livraison_offerte ? 'Offerte 🎁' : `+${transport.tarif_total}€`}
                            </span>
                          </div>
                          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                            {transport.zone_label} · forfait tout inclus (aller-retour)
                          </p>
                        </div>
                      ) : (
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          Distance non calculée — vérifiez votre adresse.
                        </p>
                      )}
                      <p className="text-xs mt-2 flex items-center gap-1.5" style={{ color: '#C58B19' }}>
                        <Clock size={11} /> Retour le lendemain avant 10H00 = pas de jour supplémentaire
                      </p>
                    </div>
                  ) : (
                    /* Pas encore d'adresse sélectionnée → vérification manuelle */
                    session.adresse.length > 3 && (
                      <div className="p-3 rounded-xl"
                        style={{ background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.2)' }}>
                        <ZoneChecker
                          compact
                          onZoneDetected={data => setSession(prev => ({ ...prev, zone_info: data }))}
                        />
                      </div>
                    )
                  )}

                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--ink)' }}>Accès / Infos</label>
                      <input type="text" placeholder="Code porte, accès véhicule..." value={session.acces}
                        onChange={e => setSession(prev => ({ ...prev, acces: e.target.value }))}
                        className="w-full px-3 py-2.5 rounded-xl text-sm"
                        style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--ink)' }}>Type de lieu</label>
                      <div className="flex gap-2">
                        {[{ id: 'interieur', label: 'Intérieur' }, { id: 'exterieur', label: 'Extérieur' }, { id: 'mixte', label: 'Mixte' }].map(l => (
                          <button key={l.id} onClick={() => setSession(prev => ({ ...prev, lieu_type: l.id }))}
                            className="flex-1 py-2.5 rounded-xl text-xs font-medium transition-all"
                            style={{
                              background: session.lieu_type === l.id ? 'var(--teal)' : '#FFFFFF',
                              border: `1px solid ${session.lieu_type === l.id ? 'var(--teal)' : 'var(--border)'}`,
                              color: session.lieu_type === l.id ? '#0B1E20' : 'var(--text-muted)'
                            }}>
                            {l.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ─────────────────── ÉTAPE 4 — Invités ─────────────────── */}
        {step === 4 && (
          <div className="animate-fade-in-up">
            <h2 className="font-display text-xl font-bold mb-5 text-center"
              style={{ color: 'var(--ink)' }}>Combien de personnes ?</h2>
            <div className="space-y-4">
              {[
                { key: 'nb_adultes', label: 'Adultes', desc: '18 ans et plus' },
                { key: 'nb_enfants', label: 'Enfants', desc: 'Moins de 18 ans' }
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-center justify-between p-5 rounded-2xl"
                  style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                  <div>
                    <p className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{label}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{desc}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setSession(prev => ({ ...prev, [key]: Math.max(0, prev[key] - 1) }))}
                      className="w-9 h-9 rounded-xl flex items-center justify-center transition-colors flex-shrink-0"
                      style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                      <Minus size={16} />
                    </button>
                    {/* Saisie directe + boutons */}
                    <input
                      type="number"
                      min="0"
                      max="500"
                      value={session[key]}
                      onChange={e => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 0 && val <= 500)
                          setSession(prev => ({ ...prev, [key]: val }));
                        else if (e.target.value === '')
                          setSession(prev => ({ ...prev, [key]: 0 }));
                      }}
                      onFocus={e => e.target.select()}
                      className="font-mono-ose font-bold text-xl text-center price-gold"
                      style={{
                        width: 64, height: 36, borderRadius: 10,
                        border: '1.5px solid var(--border)',
                        background: 'var(--bg)',
                        outline: 'none',
                        MozAppearance: 'textfield',
                      }}
                    />
                    <button onClick={() => setSession(prev => ({ ...prev, [key]: prev[key] + 1 }))}
                      className="w-9 h-9 rounded-xl flex items-center justify-center transition-colors flex-shrink-0"
                      style={{ background: 'var(--teal-mist)', color: 'var(--teal)', border: '1px solid rgba(93,188,191,0.3)' }}>
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
              ))}

              {/* Profil — visible seulement si connecté avec profil spécial */}
              {hasSpecialProfile ? (
                <div className="flex items-center gap-3 p-3 rounded-xl"
                  style={{ background: 'rgba(31,157,122,0.06)', border: '1px solid rgba(31,157,122,0.2)' }}>
                  <Check size={15} style={{ color: '#1F9D7A' }} />
                  <p className="text-xs font-semibold" style={{ color: '#1F9D7A' }}>
                    Tarif {user.profile.toUpperCase()} activé — remise appliquée automatiquement
                  </p>
                </div>
              ) : (
                <p className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
                  Tarif public appliqué. <a href="/connexion" style={{ color: 'var(--teal)' }}>Connectez-vous</a> pour bénéficier d'un tarif CE ou entreprise.
                </p>
              )}
            </div>
          </div>
        )}

        {/* ─────────────────── ÉTAPE 5 — Questions intelligentes ─────────────────── */}
        {/* ════ ÉTAPE 5 — AUDIT ÉVÉNEMENTIEL INTELLIGENT ════ */}
        {step === 5 && (
          <div className="animate-fade-in-up">
            <div className="text-center mb-5">
              <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--teal)' }}>
                Audit événementiel
              </p>
              <h2 className="font-display text-xl font-bold mb-1" style={{ color: 'var(--ink)' }}>
                {smartQuestions?.titre || 'Personnalisez votre événement'}
              </h2>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {smartQuestions?.subtitle || 'Répondez en 2 min pour une sélection parfaite — toutes les réponses sont optionnelles'}
              </p>
            </div>

            {/* Catégories de questions */}
            {smartQuestions?.categories?.length > 0 ? (
              <div className="space-y-3">
                {smartQuestions.categories.map((cat, catIdx) => {
                  const isCatOpen = expandedCat[`cat-${catIdx}`] !== false;
                  const catAnswered = cat.questions.some(q =>
                    session.smart_answers[q.id] && (
                      typeof session.smart_answers[q.id] === 'string' ? session.smart_answers[q.id] :
                      session.smart_answers[q.id].length > 0
                    )
                  );
                  return (
                    <div key={cat.id} className="rounded-2xl overflow-hidden"
                      style={{ border: `1.5px solid ${catAnswered ? 'rgba(93,188,191,0.4)' : 'var(--border)'}` }}>
                      {/* Header catégorie */}
                      <button
                        onClick={() => setExpandedCat(p => ({ ...p, [`cat-${catIdx}`]: !isCatOpen }))}
                        className="w-full flex items-center justify-between px-4 py-3 text-left"
                        style={{ background: catAnswered ? 'var(--teal-mist)' : '#FFFFFF' }}>
                        <div className="flex items-center gap-3">
                          <span style={{ fontSize: 18 }}>{cat.icon}</span>
                          <div>
                            <p className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{cat.label}</p>
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                              {cat.questions.length} question{cat.questions.length > 1 ? 's' : ''}
                              {catAnswered && <span style={{ color: '#1F9D7A', marginLeft: 6 }}>✓ Renseigné</span>}
                            </p>
                          </div>
                        </div>
                        {isCatOpen
                          ? <ChevronUp size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                          : <ChevronDown size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
                      </button>

                      {/* Questions de la catégorie */}
                      {isCatOpen && (
                        <div className="px-4 pb-4 pt-2 space-y-4"
                          style={{ background: 'var(--bg)', borderTop: '1px solid var(--border)' }}>
                          {cat.questions.map(q => {
                            const upsellTriggered = q.upsell && (
                              (q.type === 'radio' && session.smart_answers[q.id] === 'Oui') ||
                              (q.type === 'radio' && session.smart_answers[q.id] === 'Non')
                            );
                            return (
                              <div key={q.id}>
                                <p className="font-medium text-xs mb-2" style={{ color: 'var(--ink)' }}>
                                  {q.label}
                                </p>
                                {q.type === 'radio' && (
                                  <div className="flex flex-wrap gap-2">
                                    {q.options.map(opt => {
                                      const selected = session.smart_answers[q.id] === opt;
                                      return (
                                        <button key={opt} onClick={() => setAnswer(q.id, opt)}
                                          className="px-3 py-1.5 rounded-xl text-xs font-medium transition-all"
                                          style={{
                                            background: selected ? 'var(--teal)' : '#FFFFFF',
                                            border: `1.5px solid ${selected ? 'var(--teal)' : 'var(--border)'}`,
                                            color: selected ? '#0B1E20' : 'var(--text-muted)',
                                          }}>
                                          {selected && <Check size={10} className="inline mr-1" />}
                                          {opt}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                                {q.type === 'checkbox' && (
                                  <div className="flex flex-wrap gap-2">
                                    {q.options.map(opt => {
                                      const selected = (session.smart_answers[q.id] || []).includes(opt);
                                      return (
                                        <button key={opt} onClick={() => setAnswer(q.id, opt, true)}
                                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all"
                                          style={{
                                            background: selected ? 'var(--teal-mist)' : '#FFFFFF',
                                            border: `1.5px solid ${selected ? 'var(--teal)' : 'var(--border)'}`,
                                            color: selected ? '#0B4B4E' : 'var(--text-muted)',
                                          }}>
                                          {selected && <Check size={10} />}
                                          {opt}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                                {/* Upsell intelligent */}
                                {q.upsell && session.smart_answers[q.id] && (
                                  <div className="mt-2 flex items-start gap-2 p-2.5 rounded-xl"
                                    style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)' }}>
                                    <Sparkles size={12} style={{ color: '#D4AF37', flexShrink: 0, marginTop: 2 }} />
                                    <p className="text-xs" style={{ color: '#7A5E00', lineHeight: 1.5 }}>{q.upsell}</p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Fallback : anciennes questions non catégorisées */
              smartQuestions?.questions?.length > 0 ? (
                <div className="space-y-4">
                  {smartQuestions.questions.map(q => (
                    <div key={q.id} className="p-4 rounded-2xl"
                      style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                      <p className="font-semibold text-sm mb-3" style={{ color: 'var(--ink)' }}>{q.label}</p>
                      <div className="flex flex-wrap gap-2">
                        {q.options.map(opt => {
                          const selected = q.type === 'checkbox'
                            ? (session.smart_answers[q.id] || []).includes(opt)
                            : session.smart_answers[q.id] === opt;
                          return (
                            <button key={opt} onClick={() => setAnswer(q.id, opt, q.type === 'checkbox')}
                              className="px-3 py-1.5 rounded-xl text-xs font-medium transition-all"
                              style={{
                                background: selected ? 'var(--teal)' : 'var(--bg)',
                                border: `1.5px solid ${selected ? 'var(--teal)' : 'var(--border)'}`,
                                color: selected ? '#0B1E20' : 'var(--text-muted)',
                              }}>
                              {selected && <Check size={10} className="inline mr-1" />}{opt}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Sparkles size={32} className="mx-auto mb-3" style={{ color: 'var(--teal)' }} />
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                    Cliquez sur "Étape suivante" pour voir les animations suggérées.
                  </p>
                </div>
              )
            )}
          </div>
        )}

        {/* ════ ÉTAPE 6 — Boutique animations ════ */}
        {step === 6 && (
          <BoutiqueEvenementielle
            session={session}
            suggestions={suggestions}
            autoPacks={autoPacks}
            allProducts={allProducts}
            budgetRanges={budgetRanges}
            loadingSuggestions={loadingSuggestions}
            isSelected={isSelected}
            toggleProduct={toggleProduct}
            selectedEvent={selectedEvent}
            filterActive={filterActive}
            setFilterActive={setFilterActive}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            expandedCat={expandedCat}
            setExpandedCat={setExpandedCat}
            unavailableMap={step6Dispo}
          />
        )}

        {/* ════ ÉTAPE 7 — PRODUITS COMPLÉMENTAIRES ════ */}
        {step === 7 && (
          <div className="animate-fade-in-up">
            <div className="text-center mb-5">
              <h2 className="font-display text-xl font-bold mb-1" style={{ color: 'var(--ink)' }}>
                Produits complémentaires
              </h2>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                Boissons, gourmandises, son, mobilier, décoration…
              </p>
            </div>

            {/* À ajouter à votre sélection — produits à vendre associés */}
            {associesConfig.length > 0 && (
              <div className="mb-5 rounded-2xl overflow-hidden" data-testid="config-associes-section"
                style={{ border: '1.5px solid rgba(212,175,55,0.35)', background: 'rgba(212,175,55,0.05)' }}>
                <div className="px-4 py-3" style={{ background: 'rgba(212,175,55,0.12)', borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
                  <div className="flex items-center gap-2">
                    <Sparkles size={14} style={{ color: '#C58B19' }} />
                    <p className="font-semibold text-sm" style={{ color: '#7A5E00' }}>À ajouter à votre sélection</p>
                  </div>
                  <p className="text-xs mt-0.5" style={{ color: '#9A7B1A' }}>
                    Produits à acheter, recommandés avec vos animations
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3">
                  {associesConfig.map(a => {
                    const sel = isSelected(a.id);
                    const tarif = a.tarifs?.particulier;
                    return (
                      <button key={a.id} onClick={() => toggleProduct(a)}
                        data-testid={`config-associe-${a.id}`}
                        className="flex gap-3 p-3 rounded-2xl text-left w-full transition-all"
                        style={{
                          background: sel ? 'var(--teal-mist)' : '#FFFFFF',
                          border: `${sel ? '2px' : '1.5px'} solid ${sel ? 'var(--teal)' : 'rgba(212,175,55,0.3)'}`,
                          cursor: 'pointer',
                        }}>
                        <div className="relative flex-shrink-0">
                          <img src={a.images?.[0] || 'https://images.unsplash.com/photo-1531058020387-3be344556be6?w=150&q=70'}
                            alt={a.nom} className="w-12 h-12 rounded-xl object-cover" loading="lazy" />
                          {sel && (
                            <div className="absolute inset-0 rounded-xl flex items-center justify-center"
                              style={{ background: 'rgba(93,188,191,0.35)' }}>
                              <Check size={16} style={{ color: '#fff' }} />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-xs leading-snug mb-1"
                            style={{ color: sel ? '#0B4B4E' : 'var(--ink)' }}>{a.nom}</p>
                          <div className="flex items-center justify-between">
                            <span className="price-gold text-sm font-bold">{tarif > 0 ? `${tarif}€` : 'Sur devis'}</span>
                            <span className="text-xs px-1.5 py-0.5 rounded-full"
                              style={{ background: sel ? 'var(--teal)' : 'var(--bg)', color: sel ? '#fff' : 'var(--text-muted)', fontSize: 9 }}>
                              {sel ? '✓ Ajouté' : '+ Ajouter'}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Catégories complémentaires depuis la vraie DB */}
            {(() => {
              // Regrouper par catégorie step7
              const cats7 = {};
              allProducts.forEach(p => {
                const cat = getCatStep7(p);
                if (!cat) return;
                if (!cats7[cat]) cats7[cat] = [];
                cats7[cat].push(p);
              });
              const ordCats = CAT_ORDER_7.filter(c => cats7[c]);
              return ordCats.map(cat => {
                const prods = cats7[cat] || [];
                const nbSel = prods.filter(p => isSelected(p.id)).length;
                const isOpen = expandedCat[`c7_${cat}`] !== false;
                return (
                  <div key={cat} className="mb-3 rounded-2xl overflow-hidden"
                    style={{ border: `1.5px solid ${nbSel > 0 ? 'rgba(93,188,191,0.4)' : 'var(--border)'}` }}>
                    <button
                      onClick={() => setExpandedCat(p => ({ ...p, [`c7_${cat}`]: !isOpen }))}
                      className="w-full flex items-center justify-between px-4 py-3"
                      style={{ background: nbSel > 0 ? 'rgba(93,188,191,0.04)' : '#FFFFFF' }}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{cat}</p>
                        <span className="text-xs px-2 py-0.5 rounded-full"
                          style={{ background: 'var(--teal-mist)', color: '#0B4B4E' }}>{prods.length}</span>
                        {nbSel > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                            style={{ background: 'rgba(31,157,122,0.1)', color: '#1F9D7A' }}>
                            ✓ {nbSel} ajouté{nbSel > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      {isOpen ? <ChevronUp size={14} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />}
                    </button>
                    {isOpen && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3"
                        style={{ background: 'var(--bg)', borderTop: '1px solid var(--border)' }}>
                        {prods.map(p => {
                          const sel = isSelected(p.id);
                          const tarif = p.tarifs?.particulier;
                          return (
                            <button key={p.id}
                              onClick={() => toggleProduct(p)}
                              className="flex gap-3 p-3 rounded-2xl text-left w-full transition-all"
                              style={{
                                background: sel ? 'var(--teal-mist)' : '#FFFFFF',
                                border: `${sel ? '2px' : '1.5px'} solid ${sel ? 'var(--teal)' : 'var(--border)'}`,
                                cursor: 'pointer',
                              }}>
                              <div className="relative flex-shrink-0">
                                <img src={p.images?.[0] || 'https://images.unsplash.com/photo-1531058020387-3be344556be6?w=150&q=70'}
                                  alt={p.nom} className="w-12 h-12 rounded-xl object-cover" loading="lazy" />
                                {sel && (
                                  <div className="absolute inset-0 rounded-xl flex items-center justify-center"
                                    style={{ background: 'rgba(93,188,191,0.35)' }}>
                                    <Check size={16} style={{ color: '#fff' }} />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-xs leading-snug mb-1"
                                  style={{ color: sel ? '#0B4B4E' : 'var(--ink)' }}>{p.nom}</p>
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-semibold"
                                    style={{ color: tarif > 0 ? '#D4AF37' : '#0B4B4E' }}>
                                    {tarif > 0 ? `${tarif}€/jour` : 'Sur devis'}
                                  </span>
                                  <span className="text-xs px-1.5 py-0.5 rounded-full"
                                    style={{ background: sel ? 'var(--teal)' : 'var(--bg)', color: sel ? '#fff' : 'var(--text-muted)', fontSize: 9 }}>
                                    {sel ? '✓ Ajouté' : '+ Ajouter'}
                                  </span>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              });
            })()}

            <div className="p-3 rounded-xl mt-3" style={{ background: 'rgba(93,188,191,0.06)', border: '1px solid rgba(93,188,191,0.2)' }}>
              <p className="text-xs" style={{ color: '#0B4B4E', lineHeight: 1.6 }}>
                💡 Ces éléments seront intégrés à votre devis. Les tarifs exacts dépendent de la durée et des options choisies.
              </p>
            </div>
          </div>
        )}

        {/* ════ ÉTAPE 8 — RÉCAPITULATIF COMMERCIAL ════ */}
        {step === 8 && (
          <div className="animate-fade-in-up">
            <div className="text-center mb-5">
              <h2 className="font-display text-xl font-bold mb-1" style={{ color: 'var(--ink)' }}>
                Récapitulatif de votre événement
              </h2>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                Vérifiez et ajustez avant de générer votre devis
              </p>
            </div>

            {/* Infos événement */}
            <div className="p-4 rounded-2xl mb-4" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ color: 'var(--teal)' }}>
                <Calendar size={14} /> Informations événement
              </h3>
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                {[
                  { label: 'Type', value: selectedEvent?.label || session.type_evenement },
                  { label: 'Date', value: session.date ? formatDate(session.date) : 'À confirmer' },
                  { label: 'Lieu', value: session.ville_nom || session.adresse || 'À préciser' },
                  { label: 'Invités', value: `${session.nb_adultes || 0} adultes${session.nb_enfants > 0 ? ` + ${session.nb_enfants} enfants` : ''}` },
                ].map((row, i) => (
                  <div key={`row-${i}`}>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{row.label}</p>
                    <p className="font-medium text-xs" style={{ color: 'var(--ink)' }}>{row.value}</p>
                  </div>
                ))}
              </div>
              <button onClick={() => setStep(1)}
                className="mt-3 text-xs font-medium flex items-center gap-1"
                style={{ color: 'var(--teal)', background: 'none', border: 'none', cursor: 'pointer' }}>
                <Edit3 size={11} /> Modifier
              </button>
            </div>

            {/* Produits sélectionnés */}
            <div className="p-4 rounded-2xl mb-4" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
              <h3 className="font-semibold text-sm mb-3 flex items-center justify-between">
                <span className="flex items-center gap-2" style={{ color: 'var(--teal)' }}>
                  <Package size={14} /> Animations sélectionnées
                </span>
                <button onClick={() => setStep(6)}
                  className="text-xs font-medium"
                  style={{ color: 'var(--teal)', background: 'none', border: 'none', cursor: 'pointer' }}>
                  Modifier
                </button>
              </h3>

              {/* Bandeau disponibilité (planning) */}
              {availabilityLoading && (
                <p className="text-xs mb-2 flex items-center gap-1.5" style={{ color: 'var(--text-muted)' }}>
                  <RefreshCw size={11} className="animate-spin" /> Vérification de la disponibilité…
                </p>
              )}
              {!availabilityLoading && availability && !availability.all_available && (
                <div className="flex items-start gap-2 p-3 rounded-xl mb-3" data-testid="availability-warning-banner"
                  style={{ background: 'rgba(217,75,93,0.07)', border: '1px solid rgba(217,75,93,0.3)' }}>
                  <AlertCircle size={15} style={{ color: '#D94B5D', flexShrink: 0, marginTop: 1 }} />
                  <p className="text-xs font-semibold" style={{ color: '#D94B5D' }}>
                    Certains articles sont indisponibles à votre date. Modifiez votre sélection ou choisissez une autre date.
                  </p>
                </div>
              )}
              {!availabilityLoading && availability && availability.all_available && session.produits.length > 0 && (
                <p className="text-xs mb-2 flex items-center gap-1.5" data-testid="availability-ok"
                  style={{ color: '#1F9D7A' }}>
                  <Check size={12} /> Tous vos articles sont disponibles à votre date 🎉
                </p>
              )}

              {session.produits.length === 0 ? (
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                  Aucune animation sélectionnée. <button onClick={() => setStep(6)}
                    style={{ color: 'var(--teal)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}>
                    Ajouter →
                  </button>
                </p>
              ) : (
                <div className="space-y-2">
                  {session.produits.map((p, i) => {
                    const indispo = unavailableMap[p.product_id];
                    return (
                    <div key={p.product_id} data-testid={`recap-product-${p.product_id}`}
                      className="flex items-center justify-between py-1"
                      style={{ borderBottom: i < session.produits.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>{p.product_nom}</p>
                        {indispo && (
                          <p className="text-xs font-semibold flex items-center gap-1" style={{ color: '#D94B5D' }}>
                            ⚠️ Indisponible à cette date
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {indispo ? (
                          <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                            style={{ background: 'rgba(217,75,93,0.1)', color: '#D94B5D' }}>
                            Indisponible
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--teal-mist)', color: '#0B4B4E' }}>
                            Sur devis
                          </span>
                        )}
                        <button onClick={() => toggleProduct({ id: p.product_id, nom: p.product_nom })}
                          className="text-xs p-1 rounded-lg"
                          style={{ color: '#D94B5D', background: 'rgba(217,75,93,0.06)' }}>✕</button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Estimation budget */}
            {budgetRanges && (
              <div className="p-4 rounded-2xl mb-4" style={{ background: 'rgba(93,188,191,0.06)', border: '1px solid rgba(93,188,191,0.2)' }}>
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ color: '#0B4B4E' }}>
                  <TrendingUp size={14} /> Fourchette indicative
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Essentiel', value: budgetRanges.bas, color: '#1F9D7A' },
                    { label: 'Recommandé', value: budgetRanges.moyen, color: '#D4AF37' },
                    { label: 'Premium', value: budgetRanges.premium, color: '#8B5CF6' },
                  ].map((b, i) => (
                    <div key={`brange-${i}`} className="p-3 rounded-xl text-center"
                      style={{ background: `${b.color}10`, border: `1px solid ${b.color}25` }}>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{b.label}</p>
                      <p className="text-sm font-bold mt-1" style={{ color: b.color }}>{b.value}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs mt-2" style={{ color: 'rgba(11,75,78,0.6)', lineHeight: 1.5 }}>
                  {budgetRanges.note}
                </p>
              </div>
            )}

            {/* Choix de la livraison — tarif auto-calculé selon la distance */}
            <div className="p-4 rounded-2xl mb-4" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
              <h3 className="font-semibold text-sm mb-3 flex items-center justify-between">
                <span className="flex items-center gap-2" style={{ color: 'var(--teal)' }}>
                  <Truck size={14} /> Transport
                </span>
              </h3>
              <div className="space-y-2">
                {(() => {
                  const livraisonPrix = transport?.devis_requis
                    ? 'Sur devis'
                    : transport?.livraison_offerte
                      ? 'Offerte 🎁'
                      : transport
                        ? `+${transport.tarif_total}€`
                        : (session.adresse_coords ? '…' : 'Indiquez votre adresse');
                  const livraisonDesc = transport?.distance_km != null
                    ? `Livraison, installation & reprise — ${transport.distance_km} km de Figeac`
                    : 'Livraison, installation et reprise après l\'événement';
                  return [
                    { id: 'retrait', label: '🚗 Retrait sur place', desc: 'Venez récupérer le matériel à Figeac', prix: 'Gratuit' },
                    { id: 'livraison_reprise', label: '🔄 Livraison + Reprise', desc: livraisonDesc, prix: livraisonPrix },
                  ];
                })().map(opt => {
                  const sel = session.option_livraison === opt.id;
                  return (
                    <button key={opt.id}
                      data-testid={`livraison-option-${opt.id}`}
                      onClick={() => setSession(prev => ({ ...prev, option_livraison: opt.id }))}
                      className="w-full flex items-center justify-between p-3 rounded-xl text-left transition-all"
                      style={{
                        background: sel ? 'var(--teal-mist)' : 'var(--bg)',
                        border: `${sel ? '2px' : '1px'} solid ${sel ? 'var(--teal)' : 'var(--border)'}`,
                      }}>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {sel && <Check size={14} style={{ color: 'var(--teal)', flexShrink: 0 }} />}
                        <div className="min-w-0">
                          <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{opt.label}</p>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{opt.desc}</p>
                        </div>
                      </div>
                      <span className="text-xs font-bold flex-shrink-0 ml-2"
                        style={{ color: (opt.prix === 'Gratuit' || opt.prix.includes('Offerte')) ? '#1F9D7A' : '#C58B19' }}>
                        {opt.prix}
                      </span>
                    </button>
                  );
                })}
              </div>
              {session.option_livraison === 'livraison_reprise' && !session.adresse_coords && (
                <p className="text-xs mt-2" style={{ color: '#C58B19' }}>
                  ⚠ Sélectionnez votre adresse à l'étape « Lieu » pour calculer le tarif automatiquement.
                </p>
              )}
              {!session.option_livraison && (
                <p className="text-xs mt-2" style={{ color: '#C58B19' }}>
                  ⚠ Sélectionnez une option de transport
                </p>
              )}
              <p className="text-xs mt-2 flex items-center gap-1.5" style={{ color: '#C58B19' }}>
                <Clock size={11} /> Retour le lendemain avant 10H00 = pas de jour supplémentaire facturé
              </p>
            </div>

            {/* Conditions */}
            <div className="p-4 rounded-2xl" style={{ background: 'var(--bg)', border: '1px dashed var(--border)' }}>
              <p className="text-xs font-semibold mb-2" style={{ color: 'var(--ink)' }}>Conditions</p>
              {[
                'Disponibilité selon planning Show Événement',
                'Réservation confirmée après validation du devis et acompte',
                'Devis valable 7 jours',
                'Acompte de 30% obligatoire pour bloquer la date',
              ].map((c, i) => (
                <p key={`cond-${i}`} className="text-xs flex items-start gap-1.5 mb-1" style={{ color: 'var(--text-muted)' }}>
                  <span style={{ color: 'var(--teal)', flexShrink: 0, marginTop: 1 }}>·</span>{c}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* ════ ÉTAPE 9 — GÉNÉRATION DU DEVIS ════ */}
        {step === 9 && (
          <div className="animate-fade-in-up">
            <div className="text-center mb-5">
              <h2 className="font-display text-xl font-bold mb-1" style={{ color: 'var(--ink)' }}>
                Générer votre devis
              </h2>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                Renseignez vos coordonnées pour recevoir un devis personnalisé
              </p>
            </div>

            {/* Alerte 7 jours */}
            <div className="flex items-start gap-3 p-3 rounded-xl mb-5"
              style={{ background: 'rgba(197,139,25,0.08)', border: '1px solid rgba(197,139,25,0.25)' }}>
              <AlertCircle size={15} style={{ color: '#C58B19', flexShrink: 0, marginTop: 2 }} />
              <p className="text-xs" style={{ color: '#C58B19', lineHeight: 1.6 }}>
                <strong>Le devis reste valable 7 jours.</strong> Passé ce délai, les disponibilités et priorités peuvent évoluer.
              </p>
            </div>

            {/* Formulaire client */}
            <div className="p-5 rounded-2xl mb-5" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
              <h3 className="font-semibold text-sm mb-4" style={{ color: 'var(--ink)' }}>
                <User size={14} className="inline mr-1.5" />Vos coordonnées
              </h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { key: 'prenom', label: 'Prénom *', ph: 'Jean' },
                  { key: 'nom', label: 'Nom *', ph: 'Dupont' },
                  { key: 'email', label: 'Email *', ph: 'jean@exemple.fr', type: 'email', full: true },
                  { key: 'telephone', label: 'Téléphone *', ph: '06 12 34 56 78', type: 'tel' },
                ].map(f => (
                  <div key={f.key} className={f.full ? 'sm:col-span-2' : ''}>
                    <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>{f.label}</label>
                    <input type={f.type || 'text'} placeholder={f.ph}
                      value={clientForm[f.key]}
                      onChange={e => setClientForm(p => ({ ...p, [f.key]: e.target.value }))}
                      className="w-full px-3 py-2.5 rounded-xl text-sm"
                      style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                  </div>
                ))}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Notes ou demandes particulières</label>
                  <textarea rows={2} placeholder="Thème spécifique, contraintes d'accès, horaires souhaités…"
                    value={clientForm.notes}
                    onChange={e => setClientForm(p => ({ ...p, notes: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-xl text-sm resize-none"
                    style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                </div>
              </div>
            </div>

            {/* 2 choix */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className={`p-4 rounded-2xl cursor-pointer transition-all ${quoteMode === 'contact' ? 'ring-2 ring-teal-400' : ''}`}
                style={{
                  background: quoteMode === 'contact' ? 'var(--teal-mist)' : '#FFFFFF',
                  border: `${quoteMode === 'contact' ? '2px solid var(--teal)' : '1.5px solid var(--border)'}`,
                }}
                onClick={() => setQuoteMode('contact')}>
                <Phone size={20} style={{ color: 'var(--teal)', marginBottom: 8 }} />
                <p className="font-bold text-sm mb-1" style={{ color: 'var(--ink)' }}>Demander un contact</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  On vous rappelle sous 24h pour finaliser ensemble.
                  Idéal pour les projets complexes ou personnalisés.
                </p>
                {quoteMode === 'contact' && <Check size={16} style={{ color: 'var(--teal)', marginTop: 8 }} />}
              </div>
              <div className={`p-4 rounded-2xl cursor-pointer transition-all`}
                style={{
                  background: quoteMode === 'acompte' ? 'rgba(31,157,122,0.08)' : '#FFFFFF',
                  border: `${quoteMode === 'acompte' ? '2px solid #1F9D7A' : '1.5px solid var(--border)'}`,
                }}
                onClick={() => setQuoteMode('acompte')}>
                <Shield size={20} style={{ color: '#1F9D7A', marginBottom: 8 }} />
                <p className="font-bold text-sm mb-1" style={{ color: 'var(--ink)' }}>Payer l'acompte</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  Réglez 30% maintenant pour bloquer votre date.
                  Paiement sécurisé par Stripe.
                </p>
                {quoteMode === 'acompte' && <Check size={16} style={{ color: '#1F9D7A', marginTop: 8 }} />}
              </div>
            </div>
          </div>
        )}

        {/* ════ ÉTAPE 10 — CONFIRMATION & SAUVEGARDE ════ */}
        {step === 10 && (
          <div className="animate-fade-in-up">
            {createdQuote ? (
              <>
                {/* Succès */}
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ background: 'rgba(31,157,122,0.12)' }}>
                    <Check size={28} style={{ color: '#1F9D7A' }} />
                  </div>
                  <h2 className="font-display text-xl font-bold mb-2" style={{ color: 'var(--ink)' }}>
                    Devis créé avec succès !
                  </h2>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                    Référence : <strong style={{ color: 'var(--teal)' }}>{createdQuote.numero}</strong>
                  </p>
                </div>

                {/* Résumé devis */}
                <div className="p-4 rounded-2xl mb-4" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--text-muted)' }}>Total estimé</span>
                      <span className="price-gold font-bold">{createdQuote.total?.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--text-muted)' }}>Acompte (30%)</span>
                      <span style={{ color: 'var(--teal)', fontWeight: 700 }}>{createdQuote.acompte?.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between text-xs pt-2" style={{ borderTop: '1px dashed var(--border)' }}>
                      <span style={{ color: '#C58B19' }}>⏱ Valable jusqu'au</span>
                      <span style={{ color: '#C58B19', fontWeight: 600 }}>
                        {formatDate(new Date(Date.now() + 7 * 86400000))}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-3">
                  {quoteMode === 'acompte' ? (
                    <a href={`/devis/${createdQuote.id}`}
                      className="btn-teal py-3.5 rounded-xl text-sm font-bold text-center flex items-center justify-center gap-2">
                      <Shield size={16} /> Payer l'acompte — {createdQuote.acompte?.toFixed(2)}€
                    </a>
                  ) : (
                    <a href={`/devis/${createdQuote.id}`}
                      className="btn-teal py-3.5 rounded-xl text-sm font-bold text-center flex items-center justify-center gap-2">
                      <FileText size={16} /> Voir et signer mon devis
                    </a>
                  )}
                  <a href={`/devis/${createdQuote.id}`}
                    className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold"
                    style={{ background: '#FFFFFF', color: 'var(--ink)', border: '1px solid var(--border)' }}>
                    <Download size={15} /> Télécharger PDF
                  </a>
                </div>

                {/* Création compte si non connecté */}
                {!user && (
                  <div className="mt-5 p-4 rounded-2xl" style={{ background: 'rgba(93,188,191,0.06)', border: '1px solid rgba(93,188,191,0.2)' }}>
                    <p className="font-semibold text-sm mb-2" style={{ color: '#0B4B4E' }}>
                      💡 Sauvegardez votre devis
                    </p>
                    <p className="text-xs mb-3" style={{ color: 'rgba(11,75,78,0.7)', lineHeight: 1.5 }}>
                      Créez un compte en 30 secondes pour retrouver votre devis à tout moment, le signer et payer.
                    </p>
                    {!showRegister ? (
                      <button onClick={() => setShowRegister(true)}
                        className="btn-teal w-full py-2.5 rounded-xl text-sm font-bold">
                        Créer mon compte gratuitement →
                      </button>
                    ) : (
                      <div className="space-y-2">
                        {[
                          { key: 'prenom', ph: 'Prénom' },
                          { key: 'nom', ph: 'Nom' },
                          { key: 'email', ph: 'Email', type: 'email' },
                          { key: 'password', ph: 'Mot de passe (8 car. min)', type: 'password' },
                        ].map(f => (
                          <input key={f.key} type={f.type || 'text'} placeholder={f.ph}
                            value={registerForm[f.key]}
                            onChange={e => setRegisterForm(p => ({ ...p, [f.key]: e.target.value }))}
                            className="w-full px-3 py-2.5 rounded-xl text-sm"
                            style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                        ))}
                        <button
                          onClick={async () => {
                            setRegistering(true);
                            try {
                              const refCode = localStorage.getItem('ose_ref_code') || '';
                              const r = await fetch(`${BACKEND}/api/auth/register`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ ...registerForm, ...(refCode ? { ref_code: refCode } : {}) }),
                              });
                              const d = await r.json();
                              if (!r.ok) throw new Error(d.detail || 'Erreur');
                              if (refCode) localStorage.removeItem('ose_ref_code');
                              login(d.access_token, d.user);
                              
                              // Remplir le formulaire client avec les données du compte créé
                              setClientForm(prev => ({
                                ...prev,
                                email: registerForm.email,
                                nom: registerForm.nom,
                                prenom: registerForm.prenom,
                              }));
                              
                              toast.success('Compte créé ! Création du devis en cours...');
                              setShowRegister(false);
                              
                              // Créer le devis automatiquement après création du compte
                              setTimeout(() => {
                                createQuote();
                              }, 500);
                            } catch (e) { 
                              toast.error(e.message); 
                              setRegistering(false);
                            }
                          }}
                          disabled={registering || !registerForm.email || !registerForm.password}
                          className="w-full btn-teal py-2.5 rounded-xl text-sm font-bold disabled:opacity-40">
                          {registering ? 'Création…' : 'Créer mon compte et sauvegarder'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="flex justify-center py-16">
                <div className="animate-spin w-12 h-12 rounded-full"
                  style={{ border: '3px solid var(--border)', borderTopColor: 'var(--teal)' }} />
              </div>
            )}
          </div>
        )}
      </div>

    </div>

    {/* ═══════════════════════════════════════════════════════
        CTA STICKY — RENDU EN DEHORS DU SCROLL CONTAINER
        zIndex 9999 — visible sur TOUTES les étapes sans exception
        Jamais caché par Footer / Header / autre composant
    ═══════════════════════════════════════════════════════ */}
    <div
      data-testid="configurator-sticky-cta"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background: 'rgba(251,247,241,0.98)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderTop: '1.5px solid var(--border)',
        boxShadow: '0 -10px 36px rgba(26,26,46,0.10)',
        paddingTop: 10,
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
        paddingLeft: 16,
        paddingRight: 16,
      }}
    >
      <div style={{ maxWidth: 672, margin: '0 auto', display: 'flex', gap: 10 }}>

        {/* Bouton Retour — visible dès étape 2 */}
        {step > 1 && (
          <button
            onClick={() => setStep(s => s - 1)}
            data-testid="configurator-prev-step-button"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '12px 18px',
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 600,
              background: '#FFFFFF',
              color: 'var(--text-muted)',
              border: '1px solid var(--border)',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <ArrowLeft size={15} /> Retour
          </button>
        )}

        {/* Bouton principal — TOUJOURS VISIBLE, désactivé si conditions non remplies */}
        {step === 10 ? null : step < 6 ? (
          <button onClick={() => canGoNext() && setStep(s => s + 1)}
            data-testid="configurator-next-step-button"
            disabled={!canGoNext()}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 8, padding: '13px 20px', borderRadius: 12, fontSize: 14, fontWeight: 700,
              background: canGoNext() ? 'var(--teal)' : 'rgba(93,188,191,0.3)',
              color: canGoNext() ? '#0B1E20' : 'rgba(11,30,32,0.45)',
              border: 'none', cursor: canGoNext() ? 'pointer' : 'not-allowed',
              boxShadow: canGoNext() ? '0 8px 24px rgba(93,188,191,0.25)' : 'none',
              transition: 'all 0.2s ease',
            }}>
            {!canGoNext() && step === 1 && 'Sélectionnez un événement pour continuer'}
            {!canGoNext() && step === 2 && 'Choisissez une date ou cochez « sans date »'}
            {!canGoNext() && step === 3 && 'Entrez une adresse ou cochez « sans lieu »'}
            {!canGoNext() && step === 4 && 'Indiquez au moins 1 participant'}
            {canGoNext() && <>Étape suivante <ArrowRight size={16} /></>}
          </button>
        ) : step === 6 ? (
          <button onClick={() => setStep(7)} data-testid="configurator-next-step-button"
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px 20px', borderRadius: 12, fontSize: 14, fontWeight: 700, background: 'var(--teal)', color: '#0B1E20', border: 'none', cursor: 'pointer', boxShadow: '0 8px 24px rgba(93,188,191,0.25)' }}>
            {session.produits.length > 0
              ? <><span>Compléments ({session.produits.length} animation{session.produits.length > 1 ? 's' : ''})</span><ArrowRight size={16} /></>
              : <><span>Compléments</span><ArrowRight size={16} /></>}
          </button>
        ) : step === 7 ? (
          <button onClick={() => setStep(8)} data-testid="configurator-next-step-button"
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px 20px', borderRadius: 12, fontSize: 14, fontWeight: 700, background: 'var(--teal)', color: '#0B1E20', border: 'none', cursor: 'pointer', boxShadow: '0 8px 24px rgba(93,188,191,0.25)' }}>
            Récapitulatif <ArrowRight size={16} />
          </button>
        ) : step === 8 ? (
          <button onClick={() => setStep(9)} data-testid="configurator-next-step-button"
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px 20px', borderRadius: 12, fontSize: 14, fontWeight: 700, background: 'var(--teal)', color: '#0B1E20', border: 'none', cursor: 'pointer', boxShadow: '0 8px 24px rgba(93,188,191,0.25)' }}>
            Générer mon devis <ArrowRight size={16} />
          </button>
        ) : step === 9 ? (
          <button onClick={createQuote} data-testid="configurator-create-quote-button"
            disabled={!canGoNext() || creatingQuote}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 8, padding: '13px 20px', borderRadius: 12, fontSize: 14, fontWeight: 700,
              background: canGoNext() ? (quoteMode === 'acompte' ? '#1F9D7A' : 'var(--teal)') : 'rgba(93,188,191,0.3)',
              color: canGoNext() ? '#0B1E20' : 'rgba(11,30,32,0.45)',
              border: 'none', cursor: canGoNext() && !creatingQuote ? 'pointer' : 'not-allowed',
              boxShadow: canGoNext() ? '0 8px 24px rgba(93,188,191,0.25)' : 'none',
            }}>
            {creatingQuote
              ? <><RefreshCw size={15} className="animate-spin" /> Création du devis…</>
              : !canGoNext()
              ? 'Complétez le formulaire'
              : quoteMode === 'acompte'
              ? <><Shield size={15} /> Créer et payer l'acompte</>
              : <><FileText size={15} /> Créer mon devis gratuit</>}
          </button>
        ) : null}
      </div>
    </div>
    </>
  );
}
